import { formatCurrency } from 'lib/format/formatCurrencyIntl'
import { formatDecimal } from 'lib/format/formatNumber'
import { capitalise } from 'lib/string/stringUtils'
import type { IntlShape } from 'react-intl'

/**
 * Personality type gradient colors (matching TravelPersonalityStyles.ts)
 */
const PERSONALITY_COLORS: Record<string, { edge: string; center: string }> = {
  'The Sun Chaser': { edge: '#FCDCE1', center: '#E49F4E' },
  'The Wellness Wanderer': { edge: '#FDF1E3', center: '#8CD9DE' },
  'The Nature Roamer': { edge: '#E6F6F2', center: '#99E0D1' },
  'The Bond Builder': { edge: '#CCEDE6', center: '#30C5CE' },
  'The romantic retreat': { edge: '#FCDCE1', center: '#F5A388' },
  'The Trend Explorer': { edge: '#FCDCE1', center: '#C4C3F0' },
  'The Gourmet Hunter': { edge: '#FCDCE1', center: '#FDE2C3' },
  'The Elite Traveller': { edge: '#FDF1E3', center: '#CCDDDB' },
  'The Curious Explorer': { edge: '#CDF4F1', center: '#00F1BD' },
}

/**
 * Configuration for the share card
 */
interface ShareCardConfig {
  firstName: string
  personalityName: string
  personalityImageUrl?: string
  stats: {
    citiesVisited: number
    countriesVisited: number
    totalNights: number
    totalDistanceValue: number
    totalDistanceUnit: string
    totalSavings: number
    currency: string
    regionCode: string
    offersViewed: number
  }
  topDestination?: string
  isLowData: boolean
  loyalty?: {
    points: number
    statusCredits: number
    percentile: number
    tier: 'bronze' | 'silver' | 'gold' | 'platinum'
  }
  topOffer?: {
    propertyName: string
    inclusions: Array<{ description: string }>
  }
  intl: IntlShape
}

/**
 * Loyalty tier styling (from luxuryescapes.ts theme)
 */
const TIER_STYLES: Record<string, {
  textColor: string // darkForeground - used for "Top X% of our travellers" text
  gradientStart: string // for coin gradient (45deg)
  gradientMid: string
  gradientEnd: string
  strokeColor: string // normalForeground
  logoColor: string // normalContrast
}> = {
  bronze: {
    textColor: '#AD7E5F', // BRONZE[500]
    gradientStart: '#D4B5A4', // BRONZE[300]
    gradientMid: '#EFE7E3', // BRONZE[100]
    gradientEnd: '#D4B5A4', // BRONZE[300]
    strokeColor: '#D4B5A4', // BRONZE[300]
    logoColor: '#111318', // GREY[950]
  },
  silver: {
    textColor: '#868993', // GREY[500]
    gradientStart: '#BABCC2', // GREY[300]
    gradientMid: '#E7E8EA', // GREY[100]
    gradientEnd: '#BABCC2', // GREY[300]
    strokeColor: '#BABCC2', // GREY[300]
    logoColor: '#111318', // GREY[950]
  },
  gold: {
    textColor: '#96885F', // GOLD[500]
    gradientStart: '#C6BC92', // GOLD[300]
    gradientMid: '#EFE8D1', // GOLD[100]
    gradientEnd: '#C6BC92', // GOLD[300]
    strokeColor: '#C6BC92', // GOLD[300]
    logoColor: '#111318', // GREY[950]
  },
  platinum: {
    textColor: '#4C565A', // PLATINUM[700]
    gradientStart: '#24292B', // PLATINUM[900]
    gradientMid: '#677379', // PLATINUM[600]
    gradientEnd: '#24292B', // PLATINUM[900]
    strokeColor: '#677379', // PLATINUM[600]
    logoColor: '#FFFFFF', // GREY[0]
  },
}

// Design tokens matching Luxury Escapes INVERSE theme (dark background, light text)
// From primitiveColours.ts GREY scale
const COLORS = {
  // Inverse theme: background is dark
  background: '#363A45', // GREY[800]
  // Inverse theme: text colors are light
  neutralOne: '#FFFFFF', // GREY[0] - primary text (white)
  neutralTwo: '#F3F3F4', // GREY[50]
  neutralThree: '#E7E8EA', // GREY[100] - secondary text
  neutralFour: '#D4D5D8', // GREY[200] - labels
  neutralSeven: '#505460', // GREY[700] - borders in inverse
  brandPrimary: '#E41D53', // LE red/pink
}

// Typography matching Luxury Escapes design system (from luxuryescapes.ts theme)
const FONTS = {
  // Primary font: Suisse Int'l with fallbacks
  primary: '"Suisse Int\'l", "Helvetica Neue", Helvetica, Arial, sans-serif',
  // Secondary alternate: Suisse Works for decorative/italic text
  secondaryAlternate: '"Suisse Works", "Suisse Int\'l", "Helvetica Neue", Helvetica, Arial, sans-serif',
  // Font weights matching theme definitions
  weight: {
    regular: '400',
    bold: '500', // Medium weight used as bold
    superBold: '600', // SemiBold
  },
}

/**
 * Creates a shareable image card using Canvas 2D API.
 * Matches the SummarySlide design (inverse theme - dark background).
 */
export async function createShareCard(config: ShareCardConfig): Promise<Blob> {
  const {
    firstName,
    personalityName,
    personalityImageUrl,
    stats,
    topDestination,
    isLowData,
    loyalty,
    topOffer,
    intl,
  } = config

  // Canvas dimensions (9:16 aspect ratio, optimized for stories)
  const width = 540
  const height = 960
  const scale = 2 // Retina resolution
  const padding = 24

  // Ensure fonts are loaded before drawing
  await ensureFontsLoaded()

  const canvas = document.createElement('canvas')
  canvas.width = width * scale
  canvas.height = height * scale

  const ctx = canvas.getContext('2d')!
  ctx.scale(scale, scale)

  // Dark background (matching inverse theme)
  ctx.fillStyle = COLORS.background
  ctx.fillRect(0, 0, width, height)

  // Add subtle confetti-like decorations at the top
  drawConfettiBackground(ctx, width)

  // Draw logo at top
  drawLuxuryEscapesLogo(ctx, padding, 60, width / 2)

  // Content starts below the decorative header area
  let yPos = 180

  // Header: "{firstName}! here are your 2025"
  ctx.fillStyle = COLORS.neutralOne
  ctx.font = `${FONTS.weight.bold} 22px ${FONTS.primary}`
  ctx.textAlign = 'left'
  const name = capitalise(firstName?.trim() || 'Traveller')
  ctx.fillText(`${name}! here’s Your 2025`, padding, yPos)

  // "Year of Escapes" in italic (using Suisse Works)
  yPos += 40
  ctx.font = `450 32px ${FONTS.secondaryAlternate}`
  ctx.fillStyle = COLORS.neutralOne
  ctx.fillText('Year of Escapes', padding, yPos)

  // "What an incredible year!"
  yPos += 30
  ctx.font = `${FONTS.weight.regular} 16px ${FONTS.primary}`
  ctx.fillStyle = COLORS.neutralThree
  ctx.fillText('What an incredible year!', padding, yPos)

  yPos += 36

  // Travel personality card with gradient background
  const personalityCardHeight = 88
  const personalityColors = PERSONALITY_COLORS[personalityName] ?? { edge: '#505460', center: '#363A45' }

  // Draw the personality card container with border
  drawBorderedCard(ctx, padding, yPos, width - padding * 2, personalityCardHeight)

  // Draw the personality badge (gradient square)
  const badgeSize = 64
  const badgeX = padding + 12
  const badgeY = yPos + 12

  // Try to load and draw the personality image
  let personalityImageLoaded = false
  if (personalityImageUrl) {
    try {
      const img = await loadImageWithTimeout(personalityImageUrl, 3000)
      // Draw gradient background first
      drawPersonalityBadge(ctx, badgeX, badgeY, badgeSize, personalityColors.edge, personalityColors.center)
      // Draw the image on top
      ctx.drawImage(img, badgeX + 4, badgeY + 4, badgeSize - 8, badgeSize - 8)
      personalityImageLoaded = true
    } catch {
      // Image failed to load, will use gradient fallback
    }
  }

  if (!personalityImageLoaded) {
    drawPersonalityBadge(ctx, badgeX, badgeY, badgeSize, personalityColors.edge, personalityColors.center)
  }

  // "Your travel style is" label
  ctx.fillStyle = COLORS.neutralFour
  ctx.font = `${FONTS.weight.regular} 13px ${FONTS.primary}`
  ctx.textAlign = 'left'
  ctx.fillText('Your travel style is', badgeX + badgeSize + 16, yPos + 35)

  // Personality name
  ctx.fillStyle = COLORS.neutralOne
  ctx.font = `${FONTS.weight.bold} 18px ${FONTS.primary}`
  ctx.fillText(personalityName, badgeX + badgeSize + 16, yPos + 58)

  yPos += personalityCardHeight + 16

  // Stats section
  if (isLowData) {
    // Low data mode - show offers viewed and top viewed offer
    drawStatCard(ctx, padding, yPos, width - padding * 2, 72, 'VIEWED OFFERS', `${stats.offersViewed} offers`)
    yPos += 84

    // Draw top viewed offer card if available
    if (topOffer?.propertyName && topOffer?.inclusions?.length) {
      const topOfferCardHeight = drawTopOfferCard(ctx, padding, yPos, width - padding * 2, topOffer)
      yPos += topOfferCardHeight + 12
    }
  } else {
    // Full stats grid (2 columns)
    const gap = 12
    const cardWidth = (width - padding * 2 - gap) / 2
    const cardHeight = 72

    // Row 1
    drawStatCard(ctx, padding, yPos, cardWidth, cardHeight, 'DESTINATIONS EXPLORED',
      `${stats.citiesVisited} ${stats.citiesVisited === 1 ? 'destination' : 'destinations'} in ${stats.countriesVisited} ${stats.countriesVisited === 1 ? 'country' : 'countries'}`)
    drawStatCard(ctx, padding + cardWidth + gap, yPos, cardWidth, cardHeight, 'NIGHTS AWAY',
      `${stats.totalNights} nights`)
    yPos += cardHeight + gap

    // Row 2
    const hideSavings = stats.totalSavings < 100
    drawStatCard(ctx, padding, yPos, hideSavings ? (cardWidth * 2) + padding / 2 : cardWidth, cardHeight, 'DISTANCE TRAVELLED',
      `${formatDecimal(stats.totalDistanceValue, { maximumFractionDigits: 0, regionCode: stats.regionCode })} ${stats.totalDistanceUnit}`)
    if (!hideSavings) {
      drawStatCard(ctx, padding + cardWidth + gap, yPos, cardWidth, cardHeight, 'SAVINGS UNLOCKED',
        formatCurrency(stats.totalSavings, stats.currency, stats.regionCode, { format: 'roundedDollar' }))
    }
    yPos += cardHeight + gap

    // Row 3
    drawStatCard(ctx, padding, yPos, cardWidth, cardHeight, 'TOP DESTINATION',
      topDestination ?? '-')
    drawStatCard(ctx, padding + cardWidth + gap, yPos, cardWidth, cardHeight, 'VIEWED OFFERS',
      `${stats.offersViewed} offers`)
    yPos += cardHeight + gap

    // Loyalty Rewards card (full width)
    if (loyalty?.points || loyalty?.statusCredits) {
      const loyaltyCardHeight = 90
      drawLoyaltyCard(
        ctx,
        padding,
        yPos,
        width - padding * 2,
        loyaltyCardHeight,
        loyalty,
        intl,
      )
      yPos += loyaltyCardHeight + gap
    }
  }

  // Convert to blob
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error('Failed to create blob from canvas'))
        }
      },
      'image/png',
      1.0,
    )
  })
}

/**
 * Ensure fonts are loaded before drawing on canvas.
 * Uses the Font Loading API to wait for custom fonts to be ready.
 */
async function ensureFontsLoaded(): Promise<void> {
  if (typeof document === 'undefined' || !document.fonts) {
    return
  }

  try {
    // Wait for fonts to be ready
    await document.fonts.ready

    // Optionally check specific fonts we need (helps trigger load)
    const fontsToCheck = [
      `${FONTS.weight.regular} 16px ${FONTS.primary}`,
      `${FONTS.weight.bold} 16px ${FONTS.primary}`,
      `450 16px ${FONTS.secondaryAlternate}`,
    ]

    // Try loading fonts if they haven't been loaded yet
    for (const fontSpec of fontsToCheck) {
      if (!document.fonts.check(fontSpec)) {
        try {
          await document.fonts.load(fontSpec)
        } catch {
          // Font might not exist, continue with fallbacks
        }
      }
    }
  } catch {
    // Font loading API not fully supported, proceed anyway
  }
}

/**
 * Load an image with a timeout, fetching as blob to bypass CORS restrictions
 */
async function loadImageWithTimeout(url: string, timeoutMs: number): Promise<HTMLImageElement> {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), timeoutMs)

  try {
    // Fetch the image as a blob with explicit CORS mode and no credentials
    const response = await fetch(url, {
      signal: controller.signal,
      mode: 'cors',
      credentials: 'omit',
    })
    if (!response.ok) {
      throw new Error(`Image fetch failed: ${response.status}`)
    }

    const blob = await response.blob()
    clearTimeout(timeout)

    // Convert blob to data URL
    const dataUrl = await blobToDataUrl(blob)

    // Create image from data URL (no CORS issues since it's a data URL)
    return new Promise((resolve, reject) => {
      const img = new Image()

      img.onload = () => resolve(img)
      img.onerror = () => reject(new Error('Image load failed from data URL'))

      img.src = dataUrl
    })
  } catch {
    clearTimeout(timeout)

    // Fallback: try loading with crossOrigin attribute (older approach)
    return loadImageWithCrossOrigin(url, timeoutMs)
  }
}

/**
 * Fallback image loader using crossOrigin attribute
 */
function loadImageWithCrossOrigin(url: string, timeoutMs: number): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'

    const timeout = setTimeout(() => {
      reject(new Error('Image load timeout'))
    }, timeoutMs)

    img.onload = () => {
      clearTimeout(timeout)
      resolve(img)
    }

    img.onerror = () => {
      clearTimeout(timeout)
      reject(new Error('Image load failed'))
    }

    img.src = url
  })
}

/**
 * Draw the Luxury Escapes logo (simplified version)
 */
function drawLuxuryEscapesLogo(ctx: CanvasRenderingContext2D, x: number, y: number, width: number) {
  // Scale factor based on original viewBox width of 151
  const scale = width / 151

  ctx.save()
  ctx.translate(x, y)
  ctx.scale(scale, scale)
  ctx.fillStyle = '#FFFFFF'

  // Draw the LE icon (simplified palm/leaf shape)
  const iconPath = new Path2D('M13.9595 14.0527C14.0486 13.9816 14.1179 13.9295 13.9595 14.0527V14.0527Z')
  ctx.fill(iconPath)

  const iconPath2 = new Path2D('M10.5983 6.15931C10.8012 5.79449 11.1824 5.06485 10.8656 4.66686C10.5488 4.26887 9.99931 4.96061 9.83101 5.14065C9.15835 5.97075 8.71106 6.94744 8.52911 7.98341C8.9861 7.83113 9.40555 7.59065 9.76148 7.27688C10.1174 6.96311 10.4022 6.58273 10.5983 6.15931Z')
  ctx.fill(iconPath2)

  const iconPath3 = new Path2D('M13.311 14.3986C13.8703 14.0685 14.3785 13.665 14.8208 13.1999C15.3421 12.6475 15.7863 12.0328 16.1425 11.3711C15.6877 12.1829 15.0821 12.9086 14.3555 13.5126C14.8325 13.0545 15.2681 12.5584 15.6574 12.0296C14.1723 13.9532 9.81615 15.7489 7.24207 14.8724C7.10346 14.8203 7.46482 14.844 7.46482 14.8297C9.48871 14.9435 11.4784 14.2924 13.0059 13.0165C14.5333 11.7406 15.4765 9.94182 15.6326 8.00713C15.6326 7.89341 15.6326 7.74654 15.6326 7.5665C15.6603 6.02378 15.1845 4.51123 14.2714 3.23961C13.3584 1.96799 12.0537 1.00065 10.5391 0.472401C9.02442 -0.055852 7.37533 -0.118707 5.82161 0.292596C4.26788 0.703899 2.88691 1.56887 1.87113 2.76697C1.50435 3.2046 1.19204 3.6816 0.940499 4.18835C-0.0799857 6.12164 -0.256408 8.36372 0.450031 10.4215C1.15647 12.4793 2.68792 14.1842 4.70758 15.1614C4.59372 15.0951 4.47987 15.0287 4.37097 14.9529C5.12413 15.375 5.94301 15.6789 6.79655 15.8531C6.07206 15.7094 5.36995 15.4769 4.70758 15.1614C6.68765 16.2985 9.65775 16.1706 11.6923 15.3604C11.4794 15.422 11.2715 15.4741 11.0685 15.5215C10.4836 15.6826 9.8833 15.7873 9.27659 15.8342C9.87904 15.759 10.4771 15.6546 11.0685 15.5215C11.6998 15.3458 12.3052 15.0943 12.8704 14.7729C12.8061 14.8108 12.8704 14.7729 13.1328 14.6071C13.4046 14.452 13.6628 14.2761 13.905 14.0812L13.9446 14.0527C14.436 13.6647 14.878 13.2226 15.2613 12.7356C15.1128 12.8919 14.9643 13.0435 14.8059 13.2094C14.3767 13.6792 13.8721 14.0806 13.311 14.3986ZM9.6825 3.9325C9.85592 3.88291 10.0387 3.87078 10.2176 3.897C10.3965 3.92322 10.5671 3.98713 10.7171 4.08412C11.0943 4.25802 11.4311 4.50295 11.7071 4.80428C12.2813 5.51497 11.6774 6.34411 11.1676 6.90793C10.6008 7.50905 9.88863 7.96784 9.09343 8.24402C8.95977 8.2914 8.53406 8.34826 8.48456 8.44775C8.43759 8.65699 8.43759 8.8734 8.48456 9.08264C8.52911 9.40482 8.64791 9.85492 9.02908 9.94494C9.20348 9.99457 9.3882 10.001 9.5659 9.96358C9.74361 9.92618 9.90847 9.84619 10.045 9.73111C10.1816 9.61603 10.2854 9.46963 10.3467 9.30566C10.4079 9.14168 10.4247 8.96549 10.3953 8.79362C10.3878 8.712 10.3729 8.63115 10.3508 8.55199C10.3117 8.47985 10.2818 8.4035 10.2617 8.32457C10.2617 8.10662 10.5438 8.13505 10.6923 8.20138C11.0735 8.35773 11.1874 8.40511 11.1131 8.77467C11.0467 9.1931 10.828 9.57564 10.4956 9.85477C10.1632 10.1339 9.73849 10.2917 9.29639 10.3003C8.2024 10.3335 7.72718 9.28637 7.63313 8.40511C7.63313 8.46671 7.63313 8.50935 7.63313 8.40511C7.63313 8.20612 7.63313 8.31983 7.63313 8.40511C7.58143 7.50823 7.751 6.6123 8.12815 5.78977C8.44001 5.08856 8.91027 4.21204 9.6825 3.9325ZM5.90552 12.4608C5.63026 12.4218 5.34935 12.4428 5.08379 12.5224C4.86103 12.5934 4.73728 12.783 4.52442 12.8682C4.31156 12.9535 4.12841 12.8446 4.21256 12.6171C4.25216 12.5129 4.34127 12.4371 4.39077 12.3329C4.44027 12.2286 4.48977 12.1054 4.53432 11.9917C4.72338 11.4645 4.87065 10.9245 4.97489 10.3761C5.4699 8.14926 6.40549 3.66718 6.40549 3.66718C6.02564 4.01056 5.67934 4.38641 5.3709 4.79007C5.07389 5.26386 4.93529 6.28726 5.06399 6.41044C5.10765 6.44209 5.14435 6.48169 5.17198 6.52696C5.19961 6.57223 5.21762 6.62228 5.22498 6.67423C5.23234 6.72618 5.2289 6.77901 5.21487 6.82968C5.20083 6.88036 5.17647 6.92788 5.14319 6.96952C5.00459 7.16377 4.39077 6.93635 4.15316 6.73736C3.31163 6.03141 4.10366 5.17858 4.57887 4.64319C5.05409 4.10781 6.33618 3.22181 6.42529 3.11758C6.42529 3.06072 6.6827 2.57745 6.7916 2.36899C6.87116 2.18424 6.99435 2.01965 7.15152 1.88812C7.30869 1.7566 7.49557 1.66171 7.69748 1.61092C7.82619 1.57775 8.12815 1.50194 8.2321 1.61092C8.33605 1.71989 8.18755 2.0089 8.1331 2.12735C7.95203 2.53276 7.80954 2.95298 7.70738 3.3829C7.42522 4.35418 7.17771 5.33967 6.9302 6.32516C6.6827 7.31065 6.43519 8.2914 6.14808 9.26742C6.00947 9.76964 5.85107 10.2624 5.65306 10.7504C5.49673 11.2145 5.3099 11.6686 5.09369 12.1102C5.96492 11.9064 6.8708 12.1102 7.75193 12.0817C8.18363 12.0713 8.61039 11.9913 9.01423 11.8449C9.25183 11.7596 9.78645 11.3047 9.87556 11.7548C9.9214 12.0059 9.87301 12.2644 9.73894 12.4846C9.60487 12.7049 9.39376 12.8726 9.14293 12.9583C8.86319 13.051 8.56665 13.0884 8.27136 13.068C7.97606 13.0477 7.68818 12.97 7.42522 12.8398C6.94519 12.6275 6.43163 12.493 5.90552 12.4418V12.4608Z')
  ctx.fill(iconPath3)

  // Draw "LUXURY ESCAPES" text paths
  const textPaths = [
    'M28.3446 11.8732V13.0246H22.2411V2.45422H23.5034V11.8732H28.3446Z',
    'M38.3143 8.67513C38.3143 11.4231 36.5768 13.0814 33.6908 13.0814C30.8049 13.0814 29.0377 11.4231 29.0377 8.67513V2.45422H30.3V8.67513C30.3 10.7267 31.5969 11.9206 33.6908 11.9206C35.7847 11.9206 37.052 10.7267 37.052 8.67513V2.45422H38.3143V8.67513Z',
    'M49.2889 13.0246H47.7741L44.4179 8.59932L41.0864 13.0246H39.6014L43.6605 7.58067L39.8885 2.45422H41.3735L44.4179 6.53358L47.492 2.45422H48.977L45.2198 7.52855L49.2889 13.0246Z',
    'M59.9663 8.67513C59.9663 11.4231 58.2337 13.0814 55.3478 13.0814C52.4618 13.0814 50.6946 11.4231 50.6946 8.67513V2.45422H51.9767V8.67513C51.9767 10.7267 53.2687 11.9206 55.3676 11.9206C57.4665 11.9206 58.7238 10.7267 58.7238 8.67513V2.45422H59.9861L59.9663 8.67513Z',
    'M67.5203 9.65588C67.2876 9.65588 67.0253 9.68905 66.7827 9.68905H63.8571V13.0056H62.5948V2.45422H66.7926C69.5498 2.45422 71.1438 3.75242 71.1438 6.00294C71.1438 7.75597 70.2577 8.93098 68.6687 9.42846L71.2527 13.0246H69.7973L67.5203 9.65588ZM66.7827 8.52825C68.7628 8.52825 69.9211 7.6849 69.9211 6.04084C69.9211 4.39678 68.7826 3.62449 66.7827 3.62449H63.8571V8.52825H66.7827Z',
    'M77.2821 13.0246H76.0396V9.44741L71.7775 2.45422H73.0893L76.6682 7.96444L80.1928 2.45422H81.4847L77.287 9.40003L77.2821 13.0246Z',
  ]

  for (const pathData of textPaths) {
    const path = new Path2D(pathData)
    ctx.fill(path)
  }

  // ESCAPES text
  const escapesTextPaths = [
    'M84.3954 4.05565V6.89841H89.7911V8.52825H84.3954V11.4421H90.6129V13.0435H82.5342V2.45422H90.4198V4.05565H84.3954Z',
    'M99.8994 3.46816L99.1272 5.03641C98.1569 4.44656 97.0429 4.10857 95.8947 4.05566C94.8057 4.05566 94.0978 4.44891 94.0978 5.1596C94.0978 7.46697 100.038 6.23037 100.038 10.0349C100.038 11.9301 98.3005 13.0814 95.8749 13.0814C94.2068 13.0723 92.6002 12.4771 91.3604 11.4089L92.1672 9.86911C93.179 10.796 94.5059 11.3426 95.9046 11.4089C97.1966 11.4089 97.9688 10.9351 97.9688 10.125C97.9688 7.75599 92.0286 9.08261 92.0286 5.32543C92.0138 3.5108 93.6374 2.36896 96.0333 2.36896C97.4052 2.37497 98.7466 2.75637 99.8994 3.46816Z',
    'M110.829 4.04145L109.74 5.33965C109.349 4.92873 108.875 4.59773 108.347 4.3665C107.819 4.13528 107.247 4.0086 106.666 3.99408C106.144 3.97624 105.623 4.05929 105.135 4.23829C104.647 4.41729 104.202 4.68855 103.826 5.0359C103.45 5.38325 103.151 5.79956 102.946 6.25998C102.742 6.7204 102.637 7.2155 102.637 7.71572C102.637 8.21595 102.742 8.71104 102.946 9.17147C103.151 9.63189 103.45 10.0482 103.826 10.3955C104.202 10.7429 104.647 11.0142 105.135 11.1932C105.623 11.3722 106.144 11.4552 106.666 11.4374C107.819 11.4067 108.917 10.9599 109.74 10.1866L110.844 11.371C109.705 12.4527 108.176 13.075 106.572 13.1099C105.823 13.1282 105.078 13.0026 104.381 12.7404C103.684 12.4781 103.049 12.0846 102.513 11.5832C101.978 11.0817 101.553 10.4825 101.264 9.82105C100.975 9.15958 100.827 8.44931 100.83 7.7323C100.83 4.72845 103.369 2.42108 106.637 2.42108C108.199 2.42752 109.7 3.00748 110.829 4.04145Z',
    'M113.745 10.6367L112.691 13.0056H110.75L115.626 2.4353H117.532L122.358 13.0056H120.378L119.319 10.6367H113.745ZM116.522 4.3589L114.443 9.0542H118.606L116.522 4.3589Z',
    'M127.754 2.45422C130.526 2.45422 132.12 3.77137 132.12 6.06453C132.12 8.4335 130.526 9.85488 127.754 9.85488H125.239V13.0435H123.378V2.45422H127.754ZM125.239 8.23924H127.714C129.417 8.23924 130.378 7.54276 130.378 6.11191C130.378 4.68105 129.417 4.05565 127.714 4.05565H125.239V8.23924Z',
    'M135.352 4.05565V6.89841H140.748V8.52825H135.352V11.4421H141.575V13.0435H133.491V2.45422H141.377V4.05565H135.352Z',
    'M150.856 3.46814L150.084 5.0364C149.114 4.44655 148 4.10855 146.852 4.05565C145.763 4.05565 145.055 4.44889 145.055 5.15958C145.055 7.46696 150.995 6.23036 150.995 10.0349C150.995 11.9301 149.258 13.0814 146.832 13.0814C145.164 13.0723 143.557 12.4771 142.317 11.4089L143.124 9.86909C144.136 10.7959 145.463 11.3426 146.862 11.4089C148.154 11.4089 148.926 10.9351 148.926 10.1249C148.926 7.75597 142.986 9.08259 142.986 5.32541C142.986 3.51078 144.609 2.37842 147.005 2.37842C148.371 2.38457 149.707 2.76253 150.856 3.46814Z',
  ]

  for (const pathData of escapesTextPaths) {
    const path = new Path2D(pathData)
    ctx.fill(path)
  }

  ctx.restore()
}

/**
 * Draw confetti-like decorations at the top
 */
function drawConfettiBackground(ctx: CanvasRenderingContext2D, width: number) {
  const colors = ['#E41D53', '#FFB800', '#00B2A9', '#7B61FF', '#FF6B6B', '#20B140']

  // Draw scattered small shapes
  for (let i = 0; i < 40; i++) {
    ctx.globalAlpha = 0.3 + Math.random() * 0.3
    ctx.fillStyle = colors[Math.floor(Math.random() * colors.length)]

    const x = Math.random() * width
    const y = Math.random() * 120
    const size = 3 + Math.random() * 6

    ctx.beginPath()
    if (Math.random() > 0.5) {
      // Circle
      ctx.arc(x, y, size, 0, Math.PI * 2)
    } else {
      // Square rotated
      ctx.save()
      ctx.translate(x, y)
      ctx.rotate(Math.random() * Math.PI)
      ctx.fillRect(-size / 2, -size / 2, size, size)
      ctx.restore()
      ctx.beginPath()
    }
    ctx.fill()
  }
  ctx.globalAlpha = 1
}

/**
 * Draw a card with border (matching the SummarySlide style in inverse theme)
 */
function drawBorderedCard(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
) {
  const radius = 12

  // Border
  ctx.strokeStyle = COLORS.neutralSeven
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.roundRect(x, y, width, height, radius)
  ctx.stroke()
}

/**
 * Draw the personality badge with gradient
 */
function drawPersonalityBadge(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  edgeColor: string,
  centerColor: string,
) {
  const radius = 10

  // Create radial gradient for diamond-like effect
  const gradient = ctx.createRadialGradient(
    x + size / 2, y + size / 2, 0,
    x + size / 2, y + size / 2, size * 0.7,
  )
  gradient.addColorStop(0, centerColor)
  gradient.addColorStop(1, edgeColor)

  ctx.fillStyle = gradient
  ctx.beginPath()
  ctx.roundRect(x, y, size, size, radius)
  ctx.fill()

  // Subtle border
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)'
  ctx.lineWidth = 0.5
  ctx.stroke()
}

/**
 * Draw a stat card with inverse theme styling
 */
function drawStatCard(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  label: string,
  value: string,
) {
  const radius = 12
  const innerPadding = 12

  // Border
  ctx.strokeStyle = COLORS.neutralSeven
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.roundRect(x, y, width, height, radius)
  ctx.stroke()

  // Label
  ctx.fillStyle = COLORS.neutralFour
  ctx.font = `${FONTS.weight.regular} 10px ${FONTS.primary}`
  ctx.textAlign = 'left'
  ctx.fillText(label, x + innerPadding, y + 24)

  // Value - handle text wrapping for long values
  ctx.fillStyle = COLORS.neutralOne
  ctx.font = `${FONTS.weight.bold} 15px ${FONTS.primary}`

  // Truncate if too long
  let displayValue = value
  const maxWidth = width - innerPadding * 2
  while (ctx.measureText(displayValue).width > maxWidth && displayValue.length > 3) {
    displayValue = displayValue.slice(0, -4) + '...'
  }

  ctx.fillText(displayValue, x + innerPadding, y + 50)
}

const MAX_VISIBLE_INCLUSIONS = 3

/**
 * Draw the top viewed offer card (for low data mode)
 * Returns the height of the card drawn
 */
function drawTopOfferCard(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  topOffer: NonNullable<ShareCardConfig['topOffer']>,
): number {
  const radius = 12
  const innerPadding = 12
  const lineHeight = 18
  const checkmarkSize = 14

  // Calculate height based on content
  const visibleInclusions = topOffer.inclusions.slice(0, MAX_VISIBLE_INCLUSIONS)
  const remainingCount = topOffer.inclusions.length - MAX_VISIBLE_INCLUSIONS
  const hasMoreInclusions = remainingCount > 0

  // Height calculation: label + property name + inclusions + "and more" (if any)
  let contentHeight = 24 + 24 // label + property name
  if (visibleInclusions.length > 0) {
    contentHeight += 8 // gap before inclusions
    contentHeight += visibleInclusions.length * lineHeight
  }
  if (hasMoreInclusions) {
    contentHeight += lineHeight // "and more" text
  }
  const cardHeight = contentHeight + innerPadding * 2

  // Border
  ctx.strokeStyle = COLORS.neutralSeven
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.roundRect(x, y, width, cardHeight, radius)
  ctx.stroke()

  let textY = y + innerPadding + 16

  // Label: "Top Viewed offer"
  ctx.fillStyle = COLORS.neutralFour
  ctx.font = `${FONTS.weight.regular} 13px ${FONTS.primary}`
  ctx.textAlign = 'left'
  ctx.fillText('Top Viewed offer', x + innerPadding, textY)
  textY += 22

  // Property name
  ctx.fillStyle = COLORS.neutralOne
  ctx.font = `${FONTS.weight.bold} 15px ${FONTS.primary}`

  // Truncate property name if too long
  let displayName = topOffer.propertyName
  const maxWidth = width - innerPadding * 2
  while (ctx.measureText(displayName).width > maxWidth && displayName.length > 3) {
    displayName = displayName.slice(0, -4) + '...'
  }
  ctx.fillText(displayName, x + innerPadding, textY)
  textY += 12

  // Draw inclusions with checkmarks
  if (visibleInclusions.length > 0) {
    ctx.font = `${FONTS.weight.regular} 13px ${FONTS.primary}`
    ctx.fillStyle = COLORS.neutralOne

    for (const inclusion of visibleInclusions) {
      textY += lineHeight

      // Draw checkmark
      drawCheckmark(ctx, x + innerPadding, textY - checkmarkSize + 2, checkmarkSize)

      // Draw inclusion text (truncated if needed)
      let inclusionText = inclusion.description
      const inclusionMaxWidth = width - innerPadding * 2 - checkmarkSize - 8
      while (ctx.measureText(inclusionText).width > inclusionMaxWidth && inclusionText.length > 3) {
        inclusionText = inclusionText.slice(0, -4) + '...'
      }
      ctx.fillText(inclusionText, x + innerPadding + checkmarkSize + 6, textY)
    }
  }

  // Draw "and more" if there are more inclusions
  if (hasMoreInclusions) {
    textY += lineHeight
    ctx.fillStyle = COLORS.neutralOne
    ctx.font = `${FONTS.weight.bold} 13px ${FONTS.primary}`
    ctx.fillText('and more', x + innerPadding, textY)
  }

  return cardHeight
}

/**
 * Draw a checkmark icon
 */
function drawCheckmark(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
) {
  ctx.save()
  ctx.strokeStyle = COLORS.neutralOne
  ctx.lineWidth = 1.5
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'

  // Draw checkmark path
  ctx.beginPath()
  ctx.moveTo(x + size * 0.15, y + size * 0.5)
  ctx.lineTo(x + size * 0.4, y + size * 0.75)
  ctx.lineTo(x + size * 0.85, y + size * 0.25)
  ctx.stroke()

  ctx.restore()
}

/**
 * Draw the loyalty rewards card with tier badge and coin icon
 */
function drawLoyaltyCard(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  loyalty: NonNullable<ShareCardConfig['loyalty']>,
  intl: IntlShape,
) {
  const radius = 12
  const innerPadding = 12

  // Border
  ctx.strokeStyle = COLORS.neutralSeven
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.roundRect(x, y, width, height, radius)
  ctx.stroke()

  // Label
  ctx.fillStyle = COLORS.neutralFour
  ctx.font = `${FONTS.weight.regular} 10px ${FONTS.primary}`
  ctx.textAlign = 'left'
  ctx.fillText('LOYALTY REWARDS', x + innerPadding, y + 24)

  // Value: "{points} Points & {statusCredits} Status Credits" (conditionally)
  ctx.fillStyle = COLORS.neutralOne
  ctx.font = `${FONTS.weight.bold} 15px ${FONTS.primary}`
  const loyaltyParts: Array<string> = []
  if (loyalty.points > 0) {
    loyaltyParts.push(`${loyalty.points.toLocaleString()} Points`)
  }
  if (loyalty.statusCredits > 0) {
    loyaltyParts.push(`${loyalty.statusCredits} Status Credits`)
  }
  const loyaltyValue = loyaltyParts.join(' & ')
  ctx.fillText(loyaltyValue, x + innerPadding, y + 50)

  // Tier badge row: coin icon + "Top {percentile}% of our travellers"
  const tierStyle = TIER_STYLES[loyalty.tier] ?? TIER_STYLES.bronze
  const coinSize = 18
  const coinX = x + innerPadding
  const coinY = y + 62

  // Draw the loyalty tier coin
  drawLoyaltyTierCoin(ctx, coinX, coinY, coinSize, tierStyle)

  // Tier text next to the coin
  ctx.fillStyle = tierStyle.textColor
  ctx.font = `${FONTS.weight.bold} 13px ${FONTS.primary}`
  const tierText = intl.formatMessage(
    {
      id: 'xzU3kH9mzJ',
      defaultMessage: 'Top {percentile}% of our travellers',
      description: 'Loyalty badge text showing percentile rank among travellers in share card image',
    },
    { percentile: loyalty.percentile },
  )
  ctx.fillText(tierText, coinX + coinSize + 6, coinY + coinSize / 2 + 4)
}

/**
 * Draw the loyalty tier coin (circular badge with LE monogram)
 */
function drawLoyaltyTierCoin(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  tierStyle: typeof TIER_STYLES[keyof typeof TIER_STYLES],
) {
  const centerX = x + size / 2
  const centerY = y + size / 2
  const radius = size / 2

  // Create gradient (simulating 45deg linear gradient as radial for simplicity)
  const gradient = ctx.createLinearGradient(x, y + size, x + size, y)
  gradient.addColorStop(0, tierStyle.gradientStart)
  gradient.addColorStop(0.46, tierStyle.gradientMid)
  gradient.addColorStop(0.75, tierStyle.gradientEnd)

  // Draw circle with gradient fill
  ctx.beginPath()
  ctx.arc(centerX, centerY, radius, 0, Math.PI * 2)
  ctx.fillStyle = gradient
  ctx.fill()

  // Draw stroke
  ctx.strokeStyle = tierStyle.strokeColor
  ctx.lineWidth = 0.5
  ctx.stroke()

  // Draw LE monogram (scaled down version)
  drawLEMonogram(ctx, centerX, centerY, size * 0.55, tierStyle.logoColor)
}

/**
 * Draw the LE monogram logo (simplified from LineLuxuryEscapesMonogramIcon)
 */
function drawLEMonogram(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  size: number,
  color: string,
) {
  ctx.save()

  // The original viewBox is 24x24, scale to fit our size
  const scale = size / 24
  ctx.translate(centerX - size / 2, centerY - size / 2)
  ctx.scale(scale, scale)

  ctx.fillStyle = color

  // Main monogram path (from LineLuxuryEscapesMonogramIcon)
  const path = new Path2D('M16.226 5.511a2.1612 2.1612 0 0 0-.965.0692c-1.3693.5442-2.2208 2.2034-2.7343 3.5237-.6057 1.5344-1.0797 3.3141-.9217 4.9778.1667 1.7083 1.0182 3.7021 2.9801 3.6397.794-.0169 1.5567-.3177 2.154-.8497.5973-.5319.9908-1.2608 1.1115-2.0585.0922-.7047-.0702-.7939-.7549-1.1017-.2678-.116-.7681-.174-.7681.2408 0 .1014.0454.1974.0889.2892.0254.0537.0501.1059.0647.1569.0407.1457.0671.2951.079.446.1317 1.3292-.8602 2.5692-2.4315 2.1901-.6847-.1695-.8954-1.0259-.9744-1.637a2.812 2.812 0 0 1-.0263-1.2088c.0643-.1338.4555-.2285.7666-.3039.1348-.0326.2546-.0616.3262-.0886 1.4252-.5257 2.7016-1.3987 3.7176-2.5424.9173-1.0839 2.0014-2.6762.9656-4.0144a5.6553 5.6553 0 0 0-1.7776-1.3693 2.1743 2.1743 0 0 0-.9004-.3591ZM7.0184 21.8963a3.3874 3.3874 0 0 1 1.4791-.1204 8.4913 8.4913 0 0 1 2.7388.7582 3.8381 3.8381 0 0 0 1.496.4512 3.8262 3.8262 0 0 0 1.55-.1835c.4505-.1641.8295-.4844 1.0704-.9046a2.0937 2.0937 0 0 0 .2463-1.3925c-.1226-.6432-.6879-.3076-1.1465-.0355-.1489.0884-.2865.1701-.3941.2094a7.0075 7.0075 0 0 1-2.2428.4193c-.5728.0177-1.1491-.0209-1.7255-.0594-1.0223-.0684-2.045-.1368-3.0498.1085a20.5088 20.5088 0 0 0 1.0051-2.5915c.3424-.9233.6233-1.8645.8778-2.819.4872-1.8555.9612-3.7289 1.4002-5.5978a328.3597 328.3597 0 0 1 1.3825-5.629 13.6887 13.6887 0 0 1 .7637-2.3952 6.1092 6.1092 0 0 1 .0245-.0545c.1115-.2468.3178-.7036.1511-.9268-.1771-.237-.7023-.0984-.945-.0344l-.0118.0031a2.4099 2.4099 0 0 0-.9791.5287 2.462 2.462 0 0 0-.6449.9165c-.215.397-.632 1.3203-.654 1.4273-.0512.0605-.3249.2828-.6992.5869-.813.6603-2.1006 1.706-2.6145 2.3169l-.0446.053c-.8592 1.0199-2.2029 2.615-.7147 3.9301.4301.388 1.5274.8162 1.7863.446a.772.772 0 0 0 .1418-.551.766.766 0 0 0-.2734-.4972c-.2327-.2364.0175-2.2168.5486-3.0866a14.8603 14.8603 0 0 1 1.8522-2.132S7.7426 13.5732 6.8297 17.815a22.599 22.599 0 0 1-.79 3.0776c-.0251.0623-.0515.1295-.0787.1987-.0584.1488-.1204.3066-.1803.4436-.0413.0943-.099.1768-.1558.2581-.0642.0918-.1274.182-.1646.2861-.1536.4326.18.6334.5574.4728.1795-.0764.3253-.1961.4713-.3161.161-.1322.3223-.2647.5294-.3396ZM17.3899 6.9452c.5662.7582-.1141 2.1499-.4872 2.8368-.7417 1.5923-2.0058 3.033-3.7044 3.5371.3235-1.9774 1.1243-3.8424 2.3306-5.4283.3029-.3925 1.3562-1.6192 1.861-.9456Z')
  ctx.fill(path)

  ctx.restore()
}

/**
 * Share an image blob using the native share API or show save overlay as fallback
 */
export async function shareLeWrapImage(
  blob: Blob,
  options: {
    fileName?: string
  } = {},
): Promise<void> {
  const { fileName = 'my-2025-year-of-escapes.png' } = options

  // On desktop, use download directly (most reliable)
  if (isDesktop()) {
    if (await tryDownload(blob, fileName)) {
      return
    }
  }

  // On mobile, try native share with files first (works on iOS Safari, Android Chrome)
  if (await tryNativeShare(blob, fileName)) {
    return
  }

  // Fallback: show image in an overlay with save instructions (for mobile browsers without share API)
  await showImageOverlay(blob)
}

/**
 * Attempt native share with file
 * Note: Requires HTTPS (secure context) to work
 */
async function tryNativeShare(blob: Blob, fileName: string): Promise<boolean> {
  // Check if Web Share API is supported (requires HTTPS)
  if (typeof navigator === 'undefined' || !navigator.share) {
    return false
  }

  try {
    const file = new File([blob], fileName, { type: 'image/png' })

    // Check if sharing files is supported
    const canShareFiles = navigator.canShare?.({ files: [file] })
    if (!canShareFiles) {
      return false
    }

    // Only pass files - don't include title/text for best image preview on iOS
    await navigator.share({
      files: [file],
    })
    return true
  } catch(error) {
    // AbortError means user cancelled - that's fine
    if ((error as DOMException)?.name === 'AbortError') {
      return true
    }
    return false
  }
}

/**
 * Detect if we're on iOS
 */
function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

/**
 * Detect if we're on a desktop browser (not mobile/tablet)
 */
function isDesktop(): boolean {
  if (typeof navigator === 'undefined') return true

  // Check for mobile user agent patterns
  const mobilePattern = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i
  return !mobilePattern.test(navigator.userAgent)
}

/**
 * Try to trigger a download - returns false on iOS where downloads don't work
 */
async function tryDownload(blob: Blob, fileName: string): Promise<boolean> {
  // Download attribute doesn't work reliably on iOS
  if (isIOS()) {
    return false
  }

  try {
    // Convert image to data URL so it can be embedded in HTML
    const dataUrl = await blobToDataUrl(blob)

    // Create an HTML page that displays the image with save instructions
    const htmlContent = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${fileName}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      min-height: 100vh;
      background: #1a1a1a;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 20px;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    }
    p {
      color: #fff;
      margin-bottom: 16px;
      font-size: 14px;
    }
    img {
      max-width: 100%;
      max-height: calc(100vh - 80px);
      border-radius: 8px;
      box-shadow: 0 4px 24px rgba(0,0,0,0.5);
    }
  </style>
</head>
<body>
  <p>Right-click the image to save it</p>
  <img src="${dataUrl}" alt="${fileName}">
</body>
</html>`

    // Create a blob from the HTML and open it in new tab
    const htmlBlob = new Blob([htmlContent], { type: 'text/html' })
    const htmlUrl = URL.createObjectURL(htmlBlob)
    window.open(htmlUrl, '_blank')

    return true
  } catch(error) {
    console.warn('Download failed:', error)
    return false
  }
}

/**
 * Show an overlay with the image and instructions to save it
 * This is the fallback for browsers that don't support native share or download
 */
async function showImageOverlay(blob: Blob): Promise<void> {
  const dataUrl = await blobToDataUrl(blob)

  // Create overlay container
  const overlay = document.createElement('div')
  overlay.id = 'share-image-overlay'
  overlay.style.cssText = `
    position: fixed;
    inset: 0;
    z-index: 999999;
    background: rgba(0, 0, 0, 0.95);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding: 60px 20px 20px;
    box-sizing: border-box;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  `

  // Create close button
  const closeButton = document.createElement('button')
  closeButton.innerHTML = `
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path fill="currentColor" d="m13.4099 11.9999 4.3-4.29a1.0041 1.0041 0 1 0-1.42-1.42l-4.29 4.3-4.29-4.3a1.0041 1.0041 0 0 0-1.42 1.42l4.3 4.29-4.3 4.29a1.0003 1.0003 0 0 0 0 1.42.9994.9994 0 0 0 1.0946.2189.9978.9978 0 0 0 .3254-.2189l4.29-4.3 4.29 4.3a.9989.9989 0 0 0 1.0946.2189.9966.9966 0 0 0 .3254-.2189.999.999 0 0 0 .2189-1.0945.9981.9981 0 0 0-.2189-.3255l-4.3-4.29Z"/>
    </svg>
  `
  closeButton.style.cssText = `
    position: absolute;
    top: 16px;
    right: 16px;
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.7);
    width: 44px;
    height: 44px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s ease;
  `
  closeButton.onclick = () => {
    document.body.removeChild(overlay)
  }

  // Create instruction text
  const instruction = document.createElement('p')
  instruction.textContent = 'Long-press the image to save it'
  instruction.style.cssText = `
    color: white;
    font-family: ${FONTS.primary};
    font-weight: ${FONTS.weight.bold};
    font-size: 16px;
    margin-bottom: 16px;
    text-align: center;
  `

  // Create image
  const img = document.createElement('img')
  img.src = dataUrl
  img.alt = 'Year of Escapes'
  img.style.cssText = `
    max-width: 100%;
    max-height: calc(100vh - 120px);
    object-fit: contain;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  `

  // Create secondary instruction
  const hint = document.createElement('p')
  hint.textContent = 'Tap anywhere outside to close'
  hint.style.cssText = `
    color: rgba(255, 255, 255, 0.6);
    font-family: ${FONTS.primary};
    font-weight: ${FONTS.weight.regular};
    font-size: 14px;
    margin-top: 16px;
    text-align: center;
  `

  // Close when clicking overlay background
  overlay.onclick = (e) => {
    if (e.target === overlay) {
      document.body.removeChild(overlay)
    }
  }

  // Prevent body scroll while overlay is open
  const originalOverflow = document.body.style.overflow
  document.body.style.overflow = 'hidden'

  // Restore scroll when overlay is removed
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.removedNodes.forEach((node) => {
        if (node === overlay) {
          document.body.style.overflow = originalOverflow
          observer.disconnect()
        }
      })
    })
  })
  observer.observe(document.body, { childList: true })

  // Assemble and show overlay
  overlay.appendChild(closeButton)
  overlay.appendChild(instruction)
  overlay.appendChild(img)
  overlay.appendChild(hint)
  document.body.appendChild(overlay)
}

/**
 * Convert blob to data URL
 */
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result)
      } else {
        reject(new Error('Failed to convert blob to data URL'))
      }
    }
    reader.onerror = () => reject(new Error('FileReader error'))
    reader.readAsDataURL(blob)
  })
}
